import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { PlusOutlined } from '@ant-design/icons';
import { CollapseBlock } from '../../../../common/index.jsx';
import { AppBarCMS } from '../../../../basic/index.jsx';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import { useGetAssignedProjectsQuery } from '../../../../../redux/services/ing_projects.jsx';
import {
  useGetChangeRequestsQuery,
  useApproveChangeRequestMutation,
  useRejectChangeRequestMutation,
  useDeleteChangeRequestMutation,
} from '../../../../../redux/services/ing_projects_change_requests.jsx';
import IpChangeRequestsTable from '../../../../common/IngProjects/IpChangeRequestsTable.jsx';
import IpChangeRequestCreateModal from '../IngProjectsAssigned/IpChangeRequestCreateModal.jsx';

const IpChangeRequestsPage = () => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.auth.user);
  const [changeModalVisible, setChangeModalVisible] = useState(false);
  const { successMessage, apiErrorMessage } = useToastMessages();
  const { data: changeRequests, isLoading, refetch } = useGetChangeRequestsQuery(
    undefined,
    {
      pollingInterval: 15000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );
  const { data: assignedProjects } = useGetAssignedProjectsQuery(undefined, {
    skip: !user?.permissions?.['ing-projects-student'],
    refetchOnMountOrArgChange: true,
  });
  const [approveChangeRequest] = useApproveChangeRequestMutation();
  const [rejectChangeRequest] = useRejectChangeRequestMutation();
  const [deleteChangeRequest] = useDeleteChangeRequestMutation();

  const assignmentForRequest = useMemo(
    () =>
      Array.isArray(assignedProjects)
        ? assignedProjects.find(
            (assignment) => assignment?.ip_project_id && assignment?.study_program_id,
          ) || null
        : null,
    [assignedProjects],
  );
  const assignedProjectId = assignmentForRequest?.ip_project_id;
  const assignedStudyProgramId = assignmentForRequest?.study_program_id;
  const isStudent = !!user?.permissions?.['ing-projects-student'];

  const mainActions = useMemo(() => {
    if (!isStudent) {
      return [];
    }

    return [
      {
        title: t('Pridať žiadosť o zmenu projektu'),
        icon: <PlusOutlined />,
        onClick: () => setChangeModalVisible(true),
        permission: 'ing-projects-student',
        disabled: !assignedProjectId || !assignedStudyProgramId,
        disabledTooltip: t('Žiadosť môžete vytvoriť až po zverejnení priradeného projektu.'),
      },
    ];
  }, [assignedProjectId, assignedStudyProgramId, isStudent, t]);

  useEffect(() => {
    document.title = t('Žiadosti o zmenu projektu') + ' | FRI UNIZA';
    refetch();
  }, [t, refetch]);

  const handleApprove = async (id) => {
    try {
      await approveChangeRequest(id).unwrap();
      successMessage({ message: t('Žiadosť bola schválená.') });
    } catch (error) {
      apiErrorMessage(error);
    }
  };

  const handleReject = async (id) => {
    try {
      await rejectChangeRequest(id).unwrap();
      successMessage({ message: t('Žiadosť bola zamietnutá.') });
    } catch (error) {
      apiErrorMessage(error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteChangeRequest(id).unwrap();
      successMessage({ message: t('Žiadosť bola zmazaná.') });
    } catch (error) {
      apiErrorMessage(error);
    }
  };

  return (
    <>
      <AppBarCMS
        title={t('Žiadosti o zmenu projektu')}
        backUrl={-1}
        backUrlTooltip={t('Späť')}
        mainActions={mainActions}
      />

      <div style={{ margin: '10px' }}>
        <CollapseBlock title={t('Žiadosti o zmenu projektu')}>
          <IpChangeRequestsTable
            changeRequests={changeRequests || []}
            isLoading={isLoading}
            onApprove={handleApprove}
            onReject={handleReject}
            onDelete={handleDelete}
          />
        </CollapseBlock>
      </div>

      <IpChangeRequestCreateModal
        visible={changeModalVisible}
        onClose={() => setChangeModalVisible(false)}
        currentProjectId={assignedProjectId}
        currentStudyProgramId={assignedStudyProgramId}
      />
    </>
  );
};

export default IpChangeRequestsPage;
